'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CalendarClock, CalendarIcon, Clock, FolderPlus, Layers, Loader2, RefreshCw, X, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { COMMAND_V2_DIALOG_CONTENT_CLASS } from '@/components/command-team-v2/utils/dialog-styles';
import type { CommandSchedulerInitialValues, CommandSchedulerSubmitInput } from '@/types/command-scheduler';
import {
  buildCronExpression,
  buildRecurringSummaryLabel,
  buildRunAtFromLocalParts,
  clampTimeIfNeeded,
  computeNextRecurringRun,
  formatNextRunLabel,
  formatOneTimeRunLabel,
  isDateDisabledForSchedule,
  localHmToUtcHm,
  pad2,
  parseHm,
  type CommandRecurringFrequency,
  type CommandScheduleMode,
} from '@/utils/command-scheduler';

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
const WEEKDAYS = [
  { value: 1, label: 'Mon', full: 'Monday' },
  { value: 2, label: 'Tue', full: 'Tuesday' },
  { value: 3, label: 'Wed', full: 'Wednesday' },
  { value: 4, label: 'Thu', full: 'Thursday' },
  { value: 5, label: 'Fri', full: 'Friday' },
  { value: 6, label: 'Sat', full: 'Saturday' },
  { value: 0, label: 'Sun', full: 'Sunday' },
] as const;
const DEFAULT_WEEKDAYS = [1, 2, 3, 4, 5];
const DEFAULT_WEEKLY_DAY = [new Date().getDay()];

function monthDayOrdinal(day: number): string {
  const remainder = day % 100;
  if (remainder >= 11 && remainder <= 13) return `${day}th`;
  return `${day}${['th', 'st', 'nd', 'rd'][day % 10] ?? 'th'}`;
}

export type CommandSchedulerAgentOption = {
  id: string;
  name: string;
};

export type CommandSchedulerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  onSubmit: (input: CommandSchedulerSubmitInput) => void;
  dialogTitle?: string;
  agentName?: string;
  agentModelLabel?: string;
  defaultDescription?: string;
  hasExistingSession?: boolean;
  /** When provided, shows an agent picker (e.g. Scheduled page). */
  agents?: CommandSchedulerAgentOption[];
  selectedAgentId?: string | null;
  onAgentChange?: (agentId: string) => void;
  /** When provided, shows a prompt field instead of relying on composer text. */
  prompt?: string;
  onPromptChange?: (value: string) => void;
  /** Prefill the form when editing an existing schedule. */
  initialValues?: CommandSchedulerInitialValues;
  dialogSubtitle?: string;
  submitLabel?: string;
  readOnlyPrompt?: boolean;
  /** Keeps the edit dialog mounted while schedule details load. */
  isLoading?: boolean;
  /** Matches the skeleton to the schedule that will be shown. */
  loadingLayout?: 'one_time' | CommandRecurringFrequency;
  loadingMonthDay?: number;
  showSessionHint?: boolean;
};

interface ScheduleModeToggleProps {
  mode: CommandScheduleMode;
  onChange: (mode: CommandScheduleMode) => void;
  disabled?: boolean;
}

function ScheduleModeToggle({ mode, onChange, disabled }: ScheduleModeToggleProps) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-xl bg-violet-50 p-1 dark:bg-violet-950/30">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange('one_time')}
        className={cn(
          'flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all',
          mode === 'one_time'
            ? 'bg-background text-primary shadow-sm'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <Zap className="size-4 shrink-0" aria-hidden />
        One-time
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange('recurring')}
        className={cn(
          'flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all',
          mode === 'recurring'
            ? 'bg-background text-primary shadow-sm'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <RefreshCw className="size-4 shrink-0" aria-hidden />
        Recurring
      </button>
    </div>
  );
}

interface ScheduleSummaryCardProps {
  icon: 'once' | 'recurring';
  title: string;
  nextRunLabel: string;
}

function ScheduleSummaryCard({
  icon,
  title,
  nextRunLabel,
  variant = 'default',
}: ScheduleSummaryCardProps & { variant?: 'default' | 'edit' }) {
  if (variant === 'edit') {
    return (
      <div className="flex items-center gap-3 rounded-xl bg-violet-50 px-3.5 py-2.5 dark:bg-violet-950/20">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          {icon === 'once' ? <Zap className="size-4" aria-hidden /> : <RefreshCw className="size-4" aria-hidden />}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{title}</p>
          <p className="truncate text-xs text-muted-foreground">{nextRunLabel}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-xl bg-[#F3F0FF] px-4 py-3.5 dark:bg-violet-950/35">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#6B46FF] text-white">
        {icon === 'once' ? <Zap className="size-4" aria-hidden /> : <RefreshCw className="size-4" aria-hidden />}
      </div>
      <div className="min-w-0 space-y-0.5">
        <p className="text-sm font-semibold leading-snug text-foreground">{title}</p>
        <p className="truncate text-xs text-muted-foreground">{nextRunLabel}</p>
      </div>
    </div>
  );
}

interface FrequencyPickerProps {
  value: CommandRecurringFrequency;
  onChange: (value: CommandRecurringFrequency) => void;
  disabled?: boolean;
}

function FrequencyPicker({ value, onChange, disabled, isEditMode }: FrequencyPickerProps & { isEditMode?: boolean }) {
  const options: { id: CommandRecurringFrequency; label: string }[] = [
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
  ];

  return (
    <div className="flex gap-2">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option.id)}
          className={cn(
            'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
            value === option.id
              ? isEditMode
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-[#6B46FF] bg-[#6B46FF] text-white'
              : 'border-border bg-background text-muted-foreground hover:border-border/80 hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

interface WeekdayPickerProps {
  selected: number[];
  onChange: (days: number[]) => void;
  disabled?: boolean;
  frequency: 'daily' | 'weekly';
  isEditMode?: boolean;
}

interface WeekdayOption {
  value: number;
  label: string;
  full: string;
}

function WeekdayPicker({ selected, onChange, disabled, frequency, isEditMode }: WeekdayPickerProps) {
  const toggle = (day: number) => {
    if (frequency === 'weekly') {
      onChange([day]);
      return;
    }
    if (selected.includes(day)) {
      if (selected.length === 1) return;
      onChange(selected.filter((d) => d !== day));
      return;
    }
    onChange([...selected, day].sort((a, b) => a - b));
  };

  const days: WeekdayOption[] = isEditMode
    ? WEEKDAYS.map((day) => ({ value: day.value, label: day.label, full: day.full }))
    : WEEKDAY_LABELS.map((label, index) => ({
        value: index,
        label,
        full: label,
      }));

  return (
    <div
      className={cn(
        isEditMode ? 'grid grid-cols-7 gap-1.5' : 'flex justify-between gap-1.5',
      )}
      role={frequency === 'weekly' ? 'radiogroup' : 'group'}
      aria-label="Days of the week"
    >
      {days.map((day) => {
        const isSelected = selected.includes(day.value);
        return (
          <button
            key={day.value}
            type="button"
            disabled={disabled}
            role={frequency === 'weekly' ? 'radio' : undefined}
            aria-checked={frequency === 'weekly' ? isSelected : undefined}
            aria-pressed={frequency === 'weekly' ? undefined : isSelected}
            aria-label={day.full}
            onClick={() => toggle(day.value)}
            className={cn(
              'flex items-center justify-center rounded-full border text-xs font-semibold transition-colors',
              isEditMode ? 'px-1 py-1.5 text-sm' : 'size-9',
              isSelected
                ? isEditMode
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-[#6B46FF] bg-[#6B46FF] text-white'
                : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
            )}
          >
            {day.label}
          </button>
        );
      })}
    </div>
  );
}

interface TimeFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

function TimeField({ value, onChange, disabled, className }: TimeFieldProps) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5',
        className,
      )}
    >
      <Clock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <input
        type="time"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 border-0 bg-transparent text-sm text-foreground outline-none focus:ring-0 [&::-webkit-calendar-picker-indicator]:opacity-60"
      />
    </div>
  );
}

interface DateFieldProps {
  value: Date | undefined;
  onChange: (value: Date) => void;
  disabled?: boolean;
  allowPastDates?: boolean;
}

function DateField({ value, onChange, disabled, allowPastDates = false }: DateFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/30"
        >
          <CalendarIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-foreground">
            {value ? format(value, 'dd/MM/yyyy') : 'Pick a date'}
          </span>
          <CalendarIcon className="size-4 shrink-0 text-muted-foreground/70" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          disabled={(d) => !allowPastDates && isDateDisabledForSchedule(d)}
          onSelect={(d) => {
            if (!d) return;
            onChange(d);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

interface SessionPickerProps {
  newSession: boolean;
  onChange: (newSession: boolean) => void;
  hasExistingSession: boolean;
  disabled?: boolean;
}

function EditScheduleFieldsSkeleton({
  layout,
  monthDay,
  showSessionHint,
}: {
  layout: 'one_time' | CommandRecurringFrequency;
  monthDay: number;
  showSessionHint: boolean;
}) {
  return (
    <>
      <div className="rounded-xl border border-transparent px-3 py-2.5">
        <Skeleton className="h-4 w-14" />
        <Skeleton className="mt-1 h-[4.25rem] w-full" />
      </div>

      <div className="grid grid-cols-2 gap-1 rounded-xl p-1">
        <Skeleton className="h-9 rounded-lg" />
        <Skeleton className="h-9 rounded-lg" />
      </div>

      <div className="space-y-2">
        <Skeleton className="h-4 w-16" />
        <div className="grid grid-cols-2 gap-2">
          <Skeleton className="h-[38px] rounded-xl" />
          <Skeleton className="h-[38px] rounded-xl" />
        </div>
        {showSessionHint ? <Skeleton className="h-4 w-72 max-w-full" /> : null}
      </div>

      {layout === 'one_time' ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-14" />
          <div className="flex gap-2">
            <Skeleton className="h-[42px] min-w-0 flex-1 rounded-xl" />
            <Skeleton className="h-[42px] min-w-0 flex-1 rounded-xl" />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <div className="flex gap-2">
              <Skeleton className="h-[38px] flex-1 rounded-lg" />
              <Skeleton className="h-[38px] flex-1 rounded-lg" />
              <Skeleton className="h-[38px] flex-1 rounded-lg" />
            </div>
          </div>
          {layout === 'monthly' ? (
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-11 w-[92px] rounded-xl" />
              </div>
              <div className="min-w-[106px] flex-1 space-y-1.5 sm:flex-none">
                <Skeleton className="h-4 w-6" />
                <Skeleton className="h-[42px] w-full rounded-xl" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <Skeleton className="h-[34px] w-full rounded-full" />
              </div>
              <div className="min-w-[106px] space-y-1.5 sm:flex-none">
                <Skeleton className="h-4 w-6 sm:sr-only" />
                <Skeleton className="h-[42px] w-full rounded-xl" />
              </div>
            </div>
          )}
          {layout === 'monthly' && monthDay > 28 ? <Skeleton className="h-4 w-64 max-w-full" /> : null}
        </div>
      )}

      <div className="space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-11 w-full rounded-xl" />
      </div>

      <Skeleton className="h-14 w-full rounded-xl" />
    </>
  );
}

function SessionPicker({ newSession, onChange, hasExistingSession, disabled, isEditMode }: SessionPickerProps & { isEditMode?: boolean }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(true)}
        className={cn(
          'flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
          newSession
            ? isEditMode
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-[#6B46FF] bg-[#6B46FF] text-white'
            : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:bg-muted/50 hover:text-foreground',
        )}
      >
        <FolderPlus className="size-4 shrink-0" aria-hidden />
        New session
      </button>
      <button
        type="button"
        disabled={disabled || !hasExistingSession}
        onClick={() => onChange(false)}
        title={!hasExistingSession ? 'No active session — a new session will be created' : undefined}
        className={cn(
          'flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
          !newSession && hasExistingSession
            ? isEditMode
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-[#6B46FF] bg-[#6B46FF] text-white'
            : 'border-border bg-background text-muted-foreground',
          (!hasExistingSession || disabled) && 'cursor-not-allowed opacity-40',
        )}
      >
        <Layers className="size-4 shrink-0" aria-hidden />
        Current session
      </button>
    </div>
  );
}

export function CommandSchedulerDialog({
  open,
  onOpenChange,
  isSubmitting,
  onSubmit,
  dialogTitle = 'Schedule command',
  agentName,
  agentModelLabel,
  defaultDescription = '',
  hasExistingSession = false,
  agents,
  selectedAgentId,
  onAgentChange,
  prompt = '',
  onPromptChange,
  initialValues,
  dialogSubtitle,
  submitLabel,
  readOnlyPrompt = false,
  isLoading = false,
  loadingLayout = 'one_time',
  loadingMonthDay = 1,
  showSessionHint = false,
}: CommandSchedulerDialogProps) {
  const showAgentPicker = Boolean(agents?.length && onAgentChange);
  const isEditMode = readOnlyPrompt && (Boolean(initialValues) || isLoading);
  const showPromptField = Boolean(onPromptChange) || readOnlyPrompt;
  const selectedAgentName =
    agents?.find((agent) => agent.id === selectedAgentId)?.name ?? agentName;
  const [mode, setMode] = useState<CommandScheduleMode>(initialValues?.mode ?? 'one_time');
  const [customDate, setCustomDate] = useState<Date | undefined>(
    () => initialValues?.customDate ?? new Date(),
  );
  const [customTime, setCustomTime] = useState(() => {
    if (initialValues?.customTime) return initialValues.customTime;
    const n = new Date();
    return `${pad2(n.getHours())}:${pad2(n.getMinutes())}`;
  });
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [frequency, setFrequency] = useState<CommandRecurringFrequency>(
    initialValues?.frequency ?? 'weekly',
  );
  const [weekDays, setWeekDays] = useState<number[]>(
    initialValues?.weekDays ??
      (initialValues?.frequency === 'daily' ? DEFAULT_WEEKDAYS : DEFAULT_WEEKLY_DAY),
  );
  const [monthDay, setMonthDay] = useState(initialValues?.monthDay ?? 1);
  const [newSession, setNewSession] = useState(initialValues?.newSession ?? !hasExistingSession);
  const [appliedInitialValues, setAppliedInitialValues] = useState(initialValues);

  if (initialValues && initialValues !== appliedInitialValues) {
    setAppliedInitialValues(initialValues);
    setMode(initialValues.mode ?? 'one_time');
    setFrequency(initialValues.frequency ?? 'weekly');
    setWeekDays(
      initialValues.weekDays ??
        ((initialValues.frequency ?? 'weekly') === 'daily' ? DEFAULT_WEEKDAYS : DEFAULT_WEEKLY_DAY),
    );
    setMonthDay(initialValues.monthDay ?? 1);
    setDescription(initialValues.description ?? defaultDescription);
    setNewSession(initialValues.newSession ?? !hasExistingSession);
    setCustomDate(initialValues.customDate ?? new Date());
    setCustomTime(
      initialValues.customTime ??
        `${pad2(new Date().getHours())}:${pad2(new Date().getMinutes())}`,
    );
  }

  const parsedHm = useMemo(() => parseHm(customTime), [customTime]);

  const changeFrequency = useCallback((next: CommandRecurringFrequency) => {
    setFrequency(next);
    if (next === 'weekly') {
      setWeekDays((current) => (current.length === 1 ? current : DEFAULT_WEEKLY_DAY));
    } else if (next === 'daily') {
      setWeekDays(DEFAULT_WEEKDAYS);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (initialValues) {
      setMode(initialValues.mode ?? 'one_time');
      setFrequency(initialValues.frequency ?? 'weekly');
      setWeekDays(
        initialValues.weekDays ??
          ((initialValues.frequency ?? 'weekly') === 'daily' ? DEFAULT_WEEKDAYS : DEFAULT_WEEKLY_DAY),
      );
      setMonthDay(initialValues.monthDay ?? 1);
      setDescription(initialValues.description ?? defaultDescription);
      setNewSession(initialValues.newSession ?? !hasExistingSession);
      setCustomDate(initialValues.customDate ?? new Date());
      setCustomTime(initialValues.customTime ?? `${pad2(new Date().getHours())}:${pad2(new Date().getMinutes())}`);
      return;
    }
    setMode('one_time');
    setFrequency('weekly');
    setWeekDays(DEFAULT_WEEKLY_DAY);
    setMonthDay(1);
    setDescription(defaultDescription);
    setNewSession(!hasExistingSession);
    const n = new Date();
    const nextDate = new Date();
    setCustomDate(nextDate);
    setCustomTime(`${pad2(n.getHours())}:${pad2(n.getMinutes())}`);
  }, [open, defaultDescription, hasExistingSession, initialValues]);

  useEffect(() => {
    if (!open || !customDate || mode !== 'one_time' || isEditMode) return;
    const effective = clampTimeIfNeeded(customDate, customTime);
    if (effective !== customTime) setCustomTime(effective);
  }, [open, customDate, customTime, mode, isEditMode]);

  const oneTimeRunAt = useMemo(() => {
    if (!customDate) return null;
    const { h, m } = parsedHm;
    return buildRunAtFromLocalParts(customDate, h, m);
  }, [customDate, parsedHm]);

  const recurringRunAt = useMemo(
    () => computeNextRecurringRun(frequency, customTime, weekDays, monthDay),
    [frequency, customTime, weekDays, monthDay],
  );

  const summary = useMemo(() => {
    if (mode === 'one_time' && oneTimeRunAt) {
      return {
        icon: 'once' as const,
        title: formatOneTimeRunLabel(oneTimeRunAt),
        nextRun: formatNextRunLabel(oneTimeRunAt),
      };
    }
    return {
      icon: 'recurring' as const,
      title: buildRecurringSummaryLabel(frequency, customTime, weekDays, monthDay),
      nextRun: formatNextRunLabel(recurringRunAt),
    };
  }, [mode, oneTimeRunAt, frequency, customTime, weekDays, monthDay, recurringRunAt]);

  const confirm = useCallback(() => {
    const runAt = mode === 'one_time' ? oneTimeRunAt : recurringRunAt;
    if (!runAt) return;

    if (showAgentPicker && !selectedAgentId) {
      toast.error('Select an agent');
      return;
    }

    if (showPromptField && (!readOnlyPrompt || onPromptChange) && !prompt.trim()) {
      toast.error('Add a prompt to schedule');
      return;
    }

    if (
      mode === 'recurring' &&
      (frequency === 'weekly' || frequency === 'daily') &&
      weekDays.length === 0
    ) {
      toast.error('Select at least one day');
      return;
    }

    onSubmit({
      runAt,
      description: description.trim(),
      scheduleType: mode,
      cronExpression:
        mode === 'recurring'
          ? buildCronExpression(frequency, localHmToUtcHm(customTime), weekDays, monthDay)
          : null,
      newSession,
    });
  }, [
    mode,
    oneTimeRunAt,
    recurringRunAt,
    frequency,
    weekDays,
    monthDay,
    customTime,
    description,
    newSession,
    onSubmit,
    showAgentPicker,
    selectedAgentId,
    showPromptField,
    readOnlyPrompt,
    onPromptChange,
    prompt,
  ]);

  const subtitle =
    dialogSubtitle ?? [selectedAgentName, agentModelLabel].filter(Boolean).join(' · ');
  const submitDisabled =
    isSubmitting ||
    isLoading ||
    (mode === 'one_time' && !customDate) ||
    (showAgentPicker && !selectedAgentId) ||
    (showPromptField && (!readOnlyPrompt || onPromptChange) && !prompt.trim());

  const resolvedSubmitLabel =
    submitLabel ??
    (isEditMode
      ? mode === 'one_time'
        ? 'Save schedule'
        : 'Schedule recurring'
      : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(COMMAND_V2_DIALOG_CONTENT_CLASS, 'max-w-lg sm:p-5')}
      >
        <div className="flex shrink-0 items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex shrink-0 items-center justify-center rounded-[9px] bg-violet-100 p-2 dark:bg-violet-900/30">
              <CalendarClock className="size-4 text-primary" aria-hidden />
            </div>
            <div className="min-w-0 space-y-1">
              <DialogTitle className="text-left text-lg font-semibold tracking-tight">{dialogTitle}</DialogTitle>
              {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="shrink-0 text-foreground transition-colors hover:text-foreground/80"
            aria-label="Close"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <div
          className="app-scrollbar relative -mx-1 mt-4 min-h-0 flex-1 overflow-y-auto px-1 pb-1"
          aria-busy={isLoading || undefined}
        >
          <div
            className={cn('space-y-3.5', isLoading && 'pointer-events-none invisible')}
            aria-hidden={isLoading || undefined}
          >
          {showAgentPicker ? (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Agent
              </p>
              <Select
                value={selectedAgentId ?? ''}
                onValueChange={onAgentChange}
                disabled={isSubmitting}
              >
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents?.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {showPromptField ? (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Prompt
              </p>
              {readOnlyPrompt && !onPromptChange ? (
                <div className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2.5">
                  <p className="line-clamp-4 text-sm leading-relaxed text-foreground/90">{prompt}</p>
                </div>
              ) : (
                <Textarea
                  value={prompt}
                  onChange={(event) => onPromptChange?.(event.target.value)}
                  rows={3}
                  disabled={isSubmitting}
                  placeholder="What should Command do on each run?"
                  className="min-h-[88px] resize-none rounded-xl"
                />
              )}
            </div>
          ) : null}

          <ScheduleModeToggle mode={mode} onChange={setMode} disabled={isSubmitting} />

          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Session</p>
            <SessionPicker
              newSession={newSession}
              onChange={setNewSession}
              hasExistingSession={hasExistingSession}
              disabled={isSubmitting}
              isEditMode={isEditMode}
            />
            {!hasExistingSession ? (
              <p className="text-xs text-muted-foreground">No active session — a new one will be created at run time.</p>
            ) : null}
          </div>

          {mode === 'one_time' ? (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Run at</p>
              <div className="flex gap-2">
                <DateField
                  value={customDate}
                  disabled={isSubmitting}
                  allowPastDates={isEditMode}
                  onChange={(d) => {
                    setCustomDate(d);
                    setCustomTime((prev) =>
                      isEditMode ? prev : clampTimeIfNeeded(d, prev),
                    );
                  }}
                />
                <TimeField value={customTime} onChange={setCustomTime} disabled={isSubmitting} />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Repeats</p>
                <FrequencyPicker
                  value={frequency}
                  onChange={changeFrequency}
                  disabled={isSubmitting}
                  isEditMode={isEditMode}
                />
              </div>

              {frequency === 'monthly' ? (
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      On day
                    </p>
                    <Select
                      value={String(monthDay)}
                      onValueChange={(value) => setMonthDay(Number.parseInt(value, 10) || 1)}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger className="h-11 w-[92px] rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[260px]">
                        {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
                          <SelectItem key={day} value={String(day)}>
                            {monthDayOrdinal(day)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-[106px] flex-1 space-y-1.5 sm:flex-none">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">At</p>
                    <TimeField
                      value={customTime}
                      onChange={setCustomTime}
                      disabled={isSubmitting}
                      className="w-full"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1">
                      <WeekdayPicker
                        selected={weekDays}
                        onChange={setWeekDays}
                        disabled={isSubmitting}
                        frequency={frequency}
                        isEditMode={isEditMode}
                      />
                    </div>
                    <div className="min-w-[106px] space-y-1.5 sm:flex-none">
                      {isEditMode ? (
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:sr-only">
                          At
                        </p>
                      ) : null}
                      <TimeField
                        value={customTime}
                        onChange={setCustomTime}
                        disabled={isSubmitting}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}

              {frequency === 'monthly' && monthDay > 28 ? (
                <p className="text-xs text-muted-foreground">
                  Shorter months run on their last day.
                </p>
              ) : null}
            </div>
          )}

          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Description <span className="normal-case">· optional</span>
            </p>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                isEditMode ? "Add a note for this scheduled run" : "e.g. Morning pipeline briefing"
              }
              disabled={isSubmitting}
              className="h-11 rounded-xl"
            />
          </div>

          <ScheduleSummaryCard
            icon={summary.icon}
            title={summary.title}
            nextRunLabel={`Next run · ${summary.nextRun}`}
            variant={isEditMode ? 'edit' : 'default'}
          />
          </div>
          {isLoading ? (
            <div className="absolute inset-0 space-y-3.5 overflow-hidden bg-background px-1 pb-1">
              <EditScheduleFieldsSkeleton
                layout={loadingLayout}
                monthDay={loadingMonthDay}
                showSessionHint={showSessionHint}
              />
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex shrink-0 items-center gap-3 border-t pt-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting || isLoading}
            className="h-10 w-auto shrink-0 rounded-full px-6 text-sm font-medium text-iai-navy shadow-none hover:border-iai-purple hover:text-iai-purple"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={confirm}
            disabled={submitDisabled}
            className="h-10 min-w-0 flex-1 gap-2 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:border-transparent disabled:bg-iai-grey-light-2 disabled:text-iai-grey-light disabled:opacity-100 disabled:shadow-none"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {isEditMode ? 'Saving…' : 'Scheduling…'}
              </>
            ) : resolvedSubmitLabel ? (
              <>
                {mode === 'one_time' ? (
                  <Zap className="size-4" aria-hidden />
                ) : (
                  <RefreshCw className="size-4" aria-hidden />
                )}
                {resolvedSubmitLabel}
              </>
            ) : mode === 'one_time' ? (
              <>
                <Zap className="size-4" aria-hidden />
                Schedule once
              </>
            ) : (
              <>
                <RefreshCw className="size-4" aria-hidden />
                Schedule recurring
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
